import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { Router, Route, Switch, Redirect, useLocation } from 'wouter';
import { guardedAroundNav } from '@/lib/navGuard';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider, useAuthContext } from '@/lib/AuthProvider';
import { getLearnerState, clearCurrentConcept, setSessionHint, setCurrentConcept, useLearnerState } from '@/lib/learnerState';
import { missionPath, navPath, decodeChapterId, notesChapterPath, notesPagePath } from '@/lib/routes';

/**
 * App root + routing.
 *
 * The root "/" is the public landing page, "/login" the sign-in screen; a signed-in student
 * is sent from either to "/home". Guarded routes bounce a signed-out visitor to "/login".
 * Routes are lazy-loaded so only the landed screen's code ships (route-based code
 * splitting → initial-JS budget). Home fetches its data from the backend via TanStack Query
 * (HomeRoute); the learning session runs live via LiveKit (LearningSession lazily pulls the
 * whole LiveKit stack so it never reaches this initial bundle). The QueryClient is mounted
 * once here; AuthProvider lives inside it so signOut's queryClient.clear() has the client.
 */
const WebsitePage = lazy(() => import('@/pages/website').then((m) => ({ default: m.WebsitePage })));
const SignInPage = lazy(() => import('@/pages/sign-in').then((m) => ({ default: m.SignInPage })));
const SignupPage = lazy(() => import('@/pages/signup').then((m) => ({ default: m.SignupPage })));
const HomeRoute = lazy(() => import('@/pages/home').then((m) => ({ default: m.HomeRoute })));
const LearningSession = lazy(() => import('@/pages/learning-session').then((m) => ({ default: m.LearningSession })));
const RosterRoute = lazy(() => import('@/pages/roster').then((m) => ({ default: m.RosterRoute })));
const NotesLibraryRoute = lazy(() => import('@/pages/notes/library/NotesLibraryRoute').then((m) => ({ default: m.NotesLibraryRoute })));
const NotesChapterRoute = lazy(() => import('@/pages/notes/chapter/NotesChapterRoute').then((m) => ({ default: m.NotesChapterRoute })));
const AwardsRoute = lazy(() => import('@/pages/awards').then((m) => ({ default: m.AwardsRoute })));
const ExploreRoute = lazy(() => import('@/pages/explore').then((m) => ({ default: m.ExploreRoute })));
const PreferencesRoute = lazy(() => import('@/pages/preferences').then((m) => ({ default: m.PreferencesRoute })));
const RecallRoute = lazy(() => import('@/pages/recall').then((m) => ({ default: m.RecallRoute })));
const EventClosedPage = lazy(() => import('@/pages/event-signup').then((m) => ({ default: m.EventClosedPage })));

/** The warm line Home shows after a bounce off /learn. In-memory, so a manual reload — which
 * has no bounce to explain — correctly clears it. */
const NO_LESSON_HINT = "Let's find that lesson again — pick a concept to start.";

/**
 * LearnGuard — /learn needs a concept AND a learning objective, both of which live only in the
 * in-memory learnerState. A page reload on /learn empties that store, so without this guard the
 * session would fire POST /tutoring/start with no ids: a guaranteed 422 into an error screen
 * whose "Try again" re-sends the identical bad request forever.
 *
 * The ids stay OUT of the URL by product decision, so the fix is to bounce Home rather than to
 * reconstruct them. A <Redirect> renders nothing, so the explanation can't be handed down as a
 * prop — it goes through learnerState (the same App↔Home channel the current concept uses) and
 * Home renders it. Setting it in an effect keeps the render pure; the copy is set before the
 * redirect commits, so Home never paints a frame without it.
 */
function LearnGuard({ children }) {
  const { conceptId, loId } = getLearnerState().currentConcept ?? {};
  const startable = Boolean(conceptId) && Boolean(loId);

  useEffect(() => {
    if (!startable) setSessionHint(NO_LESSON_HINT);
  }, [startable]);

  if (!startable) return <Redirect to="/home" />;
  return children;
}

/**
 * LearnSessionRoute — the /learn page, KEYED on learnerState's `sessionNonce`.
 *
 * The key is load-bearing, like the /mission route's. A session can end on an "Up next" card that
 * starts the next objective from inside /learn. wouter keeps the same element for the same path,
 * so without a key every ref the page holds (the start latch, the end latches, the end phase)
 * would carry into the next session — it would skip its own start and replay the last one's
 * results. `setCurrentConcept` bumps the nonce, so each start is a fresh mount.
 *
 * It SUBSCRIBES to learnerState (App reads it without subscribing), because a start from inside
 * /learn changes no route: navigating to the path it is already on re-renders nothing, so the
 * store change itself has to.
 */
function LearnSessionRoute(props) {
  const { currentConcept, sessionNonce } = useLearnerState();
  return (
    <LearningSession
      key={sessionNonce}
      conceptId={currentConcept?.conceptId}
      loId={currentConcept?.loId}
      {...props}
    />
  );
}

/**
 * SignupGuard — keeps an already-signed-in student out of /signup, WITHOUT evicting a signup
 * that is still finishing.
 *
 * These look like the same condition and are not. Redeeming the invite code calls
 * `setSession`, so `authed` flips true while the flow still has ~9s of celebration and loader
 * left to play. A plain `authed ? <Redirect/>` unmounted the whole screen mid-confetti and
 * dropped the student straight onto Home — the last two beats of the flow never rendered.
 *
 * The distinction is whether the student was ALREADY authenticated when they arrived. That is
 * a mount-time fact, so it is latched in a ref: a session minted later by this very screen
 * must not retroactively redirect it.
 */
function SignupGuard({ authed, children }) {
  const wasAuthedOnArrival = useRef(authed);
  if (wasAuthedOnArrival.current) return <Redirect to="/home" />;
  return children;
}

/**
 * Routes — rendered inside AuthProvider so useAuthContext() is available.
 * Guards gate on the reactive `status` from context so login/logout re-renders them.
 */
function Routes() {
  const [, navigate] = useLocation();
  const { status, username, signIn, signOut } = useAuthContext();

  const authed = status === 'authenticated';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <Suspense fallback={null}>
      <Switch>
        <Route path="/">
          {authed ? <Redirect to="/home" /> : (
            <WebsitePage onJoin={() => navigate('/login')} onSignUp={() => navigate('/signup')} />
          )}
        </Route>
        <Route path="/login">
          {authed ? (
            <Redirect to="/home" />
          ) : (
            <SignInPage
              onSignIn={signIn}
              onSignedIn={() => navigate('/home')}
              onCreateAccount={() => navigate('/signup')}
            />
          )}
        </Route>
        <Route path="/signup">
          <SignupGuard authed={authed}>
            {/* The session goes live BEFORE this screen is finished: redeeming the invite code
                mints the tokens, and the flow then plays its celebration and "building your
                world" loader on top of an authenticated session. `onDone` is what leaves. */}
            <SignupPage onDone={() => navigate('/home')} />
          </SignupGuard>
        </Route>
        <Route path="/home">
          {!authed ? (
            <Redirect to="/login" />
          ) : (
            <HomeRoute
              onStartConcept={() => navigate('/learn')}
              onStartRecall={(conceptId, setId) => navigate(missionPath(conceptId, setId))}
              onNavigate={(key) => navigate(navPath(key))}
              onOpenNotes={(chapterId, page) =>
                navigate(page ? notesPagePath(chapterId, page) : notesChapterPath(chapterId))}
              studentId={username}
            />
          )}
        </Route>
        <Route path="/roster">
          {!authed ? (
            <Redirect to="/login" />
          ) : (
            <RosterRoute
              onStartConcept={() => navigate('/learn')}
              onNavigate={(key) => navigate(navPath(key))}
            />
          )}
        </Route>
        <Route path="/awards">
          {!authed ? (
            <Redirect to="/login" />
          ) : (
            <AwardsRoute onNavigate={(key) => navigate(navPath(key))} />
          )}
        </Route>
        <Route path="/notes">
          {!authed ? <Redirect to="/login" /> : <NotesLibraryRoute onNavigate={(key) => navigate(navPath(key))} />}
        </Route>
        {/* One route for the overview, every page and Exam Tips. Keyed by chapter ONLY, so the
            frame and page list persist across page turns (spec §3.1). */}
        <Route path="/notes/:chapterId/*?">
          {(params) =>
            !authed ? (
              <Redirect to="/login" />
            ) : (
              <NotesChapterRoute
                key={params.chapterId}
                chapterId={decodeChapterId(params.chapterId)}
                rest={params['*']}
                onNavigate={(key) => navigate(navPath(key))}
                onStartLearning={() => navigate('/learn')}
                studentId={username}
              />
            )
          }
        </Route>
        <Route path="/explore/:view?">
          {!authed ? <Redirect to="/" /> : (
            <ExploreRoute key={username} onNavigate={(key) => navigate(navPath(key))} />
          )}
        </Route>
        <Route path="/preferences">
          {!authed ? (
            <Redirect to="/login" />
          ) : (
            <PreferencesRoute
              onClose={() => navigate('/home')}
              onSignOut={handleSignOut}
              onNavigate={(key) => navigate(navPath(key))}
            />
          )}
        </Route>
        <Route path="/learn">
          {!authed ? (
            <Redirect to="/login" />
          ) : (
            <LearnGuard>
              <LearnSessionRoute
                studentId={username}
                onEnd={() => {
                  clearCurrentConcept();
                  navigate('/home');
                }}
                // The "Up next" card at the end of a session: start that objective directly,
                // under the same two-id contract as Home's start path. The nonce bump in
                // setCurrentConcept is what remounts the session (see LearnSessionRoute).
                onStartNext={(next) => {
                  if (!next?.conceptId || !next?.loId) {
                    console.warn('onStartNext: suggestion missing an id — staying put', next);
                    return;
                  }
                  setCurrentConcept({
                    conceptId: next.conceptId,
                    name: next.conceptName,
                    subject: next.subject,
                    loId: next.loId,
                    loName: next.loName,
                  });
                  // REPLACE, not push: we are already on /learn. A push would add an identical
                  // entry, so the student's first Back would land on the same path, wouter would
                  // not re-render, and Back would appear to do nothing (one more dead press per
                  // chained "Up next"). The nonce bump above is what remounts the session.
                  navigate('/learn', { replace: true });
                }}
                // Deliberately NOT onEnd. The side-effects are the same, but the meaning isn't:
                // onEnd ends a session that ran, while this fires when one never started, and it
                // additionally sets the hint so Home can explain the bounce. Folding that into
                // onEnd would make every normal session end tell the student to find their
                // lesson again.
                onGoHome={() => {
                  setSessionHint(NO_LESSON_HINT);
                  clearCurrentConcept();
                  navigate('/home');
                }}
              />
            </LearnGuard>
          )}
        </Route>
        <Route path="/mission/:conceptId/:setId">
          {(params) =>
            !authed ? (
              <Redirect to="/login" />
            ) : (
              // `key` is load-bearing, not cosmetic. The results screen's "Up next" card
              // navigates mission -> mission, and wouter swaps route PARAMS without
              // unmounting the element. Without a param-derived key, every bit of
              // RecallRoute's internal state (loaded, quizDone, attemptIdRef, startFailed…)
              // would survive into the next mission: it would skip the loader, show the
              // previous mission's summary, and — because attemptIdRef is still truthy —
              // never create an attempt of its own. Keying on the pair forces a clean remount.
              <RecallRoute
                key={missionPath(params.conceptId, params.setId)}
                conceptId={params.conceptId}
                setId={params.setId}
                onExit={() => navigate('/home')}
              />
            )
          }
        </Route>
        {/* Public event page: no auth gate either way — parents land here from shared links.
            Registrations are closed, so it's the closed page; EventSignupRoute is the open one. */}
        <Route path="/events/:slug">
          <EventClosedPage onSignUp={() => navigate('/signup')} />
        </Route>
        <Route>
          <Redirect to="/" />
        </Route>
      </Switch>
    </Suspense>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {/* aroundNav only: the location hook is inherited (browser in the app, memory in tests).
            It lets a screen hold navigation until the student confirms (navGuard.js). */}
        <Router aroundNav={guardedAroundNav}>
          <Routes />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}
